/**
 * V91.3: 프로젝트 태그 마스터 관리 패널
 *
 * OrgProjectsPage 내부 탭으로 사용되는 재사용 컴포넌트
 *
 * 태그 종류:
 * - workType: 업무형태 (QA, 로컬, 개발 등)
 * - projectDomain: 프로젝트 분야 (게임, 블록체인, 웹서비스 등)
 *
 * 기능:
 * - 태그 목록 조회 (검색, 필터)
 * - 태그 생성/수정 (upsert)
 * - 태그 비활성화 (archive)
 * - 전사 공용 (companyCode 없음)
 * - 대소문자 무시 중복 체크 (labelNormalized)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Edit2, Archive, Search, X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';
import { useProjectTags, UpsertProjectTagInput } from '../../../shared/hooks/useProjectTags';
import {
  ProjectTagMaster,
  ProjectTagType,
  PROJECT_TAG_TYPE_LABELS,
} from '../../../shared/types/project';

// ===== Props =====

interface ProjectTagMasterPanelProps {
  /**
   * 태그 변경 시 호출되는 콜백
   * - OrgProjectsPage의 tagMap 캐시 갱신에 사용
   */
  onTagsChange?: () => void;
}

// ===== 메인 컴포넌트 =====

export function ProjectTagMasterPanel({ onTagsChange }: ProjectTagMasterPanelProps) {
  // 탭 상태
  const [activeTab, setActiveTab] = useState<ProjectTagType>('workType');

  // 태그 데이터
  const [workTypeTags, setWorkTypeTags] = useState<ProjectTagMaster[]>([]);
  const [projectDomainTags, setProjectDomainTags] = useState<ProjectTagMaster[]>([]);

  // 검색
  const [searchQuery, setSearchQuery] = useState('');

  // V104.1: 페이지네이션
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // 모달
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState<ProjectTagMaster | null>(null);

  // 삭제 확인
  const [archiveConfirm, setArchiveConfirm] = useState<ProjectTagMaster | null>(null);

  const { listTags, upsertTag, archiveTag, isLoading, error } = useProjectTags();

  // 데이터 로드 (V91: 전사 공용, includeArchived=true for 마스터 관리)
  const loadData = useCallback(async () => {
    try {
      const [workTypes, domains] = await Promise.all([
        listTags({ type: 'workType', includeArchived: true }),
        listTags({ type: 'projectDomain', includeArchived: true }),
      ]);
      setWorkTypeTags(workTypes);
      setProjectDomainTags(domains);
    } catch (err) {
      console.error('[V91.3] 태그 목록 로드 실패:', err);
    }
  }, [listTags]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 현재 탭의 태그 목록
  const currentTags = activeTab === 'workType' ? workTypeTags : projectDomainTags;

  // 검색 필터 + V104.1: 최신 등록순 정렬
  const filteredTags = useMemo(() => {
    let filtered = searchQuery.trim()
      ? currentTags.filter((tag) => tag.label.toLowerCase().includes(searchQuery.toLowerCase()))
      : currentTags;

    // V104.1: 최신 등록순 정렬 (createdAt DESC)
    return [...filtered].sort((a, b) => {
      const getTime = (ts: any): number => {
        if (!ts) return 0;
        if (typeof ts.toMillis === 'function') return ts.toMillis();
        if (typeof ts.seconds === 'number') return ts.seconds * 1000;
        if (ts instanceof Date) return ts.getTime();
        return 0;
      };
      return getTime(b.createdAt) - getTime(a.createdAt);
    });
  }, [currentTags, searchQuery]);

  // V104.1: 페이지네이션 계산
  const totalPages = Math.ceil(filteredTags.length / ITEMS_PER_PAGE);
  const paginatedTags = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTags.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredTags, currentPage]);

  // V104.1: 탭/검색 변경 시 페이지 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  // 생성/수정 핸들러 (V91: companyCode 제거)
  const handleSave = async (label: string) => {
    try {
      const input: UpsertProjectTagInput = editingTag
        ? { id: editingTag.id, type: activeTab, label }
        : { type: activeTab, label };

      await upsertTag(input);
      setShowModal(false);
      setEditingTag(null);
      await loadData();
      // V91.3: 상위 컴포넌트에 변경 알림
      onTagsChange?.();
    } catch (err: any) {
      alert(err.message || '저장 실패');
    }
  };

  // 비활성화 핸들러
  const handleArchive = async (tag: ProjectTagMaster) => {
    try {
      await archiveTag(tag.id);
      setArchiveConfirm(null);
      await loadData();
      // V91.3: 상위 컴포넌트에 변경 알림
      onTagsChange?.();
    } catch (err: any) {
      alert(err.message || '비활성화 실패');
    }
  };

  return (
    <div>
      {/* 패널 설명 */}
      <div style={{ marginBottom: '20px' }}>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
          프로젝트에 사용할 업무형태/분야 태그를 관리합니다. <strong>(전사 공용)</strong>
        </p>
        <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '4px' }}>
          동일한 이름의 태그는 등록할 수 없습니다. (대소문자 무시)
        </p>
      </div>

      {/* 서브탭: workType / projectDomain */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '16px',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '12px',
        }}
      >
        {(['workType', 'projectDomain'] as ProjectTagType[]).map((type) => (
          <button
            key={type}
            onClick={() => {
              setActiveTab(type);
              setSearchQuery('');
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: activeTab === type ? '#3b82f6' : '#f3f4f6',
              color: activeTab === type ? '#fff' : '#374151',
              fontWeight: activeTab === type ? 600 : 400,
              transition: 'all 0.15s',
            }}
          >
            {PROJECT_TAG_TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      {/* 검색 + 추가 버튼 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
            />
            <input
              type="text"
              placeholder="태그 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '8px 8px 8px 32px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                width: '200px',
                fontSize: '14px',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                }}
              >
                <X size={14} color="#9ca3af" />
              </button>
            )}
          </div>

          <button
            onClick={loadData}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            새로고침
          </button>
        </div>

        <button
          onClick={() => {
            setEditingTag(null);
            setShowModal(true);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '14px',
          }}
        >
          <Plus size={16} />
          태그 추가
        </button>
      </div>

      {/* 에러 표시 */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            marginBottom: '16px',
          }}
        >
          {error.message}
        </div>
      )}

      {/* 태그 목록 테이블 */}
      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                  width: '50px',
                }}
              >
                #
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                태그명
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                  width: '100px',
                }}
              >
                상태
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                  width: '120px',
                }}
              >
                작업
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && filteredTags.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  로딩 중...
                </td>
              </tr>
            ) : filteredTags.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  {searchQuery ? '검색 결과가 없습니다.' : '등록된 태그가 없습니다.'}
                </td>
              </tr>
            ) : (
              paginatedTags.map((tag, index) => (
                <tr
                  key={tag.id}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: tag.status === 'archived' ? '#f9fafb' : '#fff',
                  }}
                >
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '13px',
                      color: '#6b7280',
                    }}
                  >
                    {/* V104.1: 페이지 기준 번호 */}
                    {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: tag.status === 'archived' ? '#9ca3af' : '#111827',
                      textDecoration: tag.status === 'archived' ? 'line-through' : 'none',
                    }}
                  >
                    {tag.label}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: tag.status === 'active' ? '#dcfce7' : '#f3f4f6',
                        color: tag.status === 'active' ? '#166534' : '#6b7280',
                      }}
                    >
                      {tag.status === 'active' ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                      <button
                        onClick={() => {
                          setEditingTag(tag);
                          setShowModal(true);
                        }}
                        title="수정"
                        style={{
                          padding: '6px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        <Edit2 size={14} color="#6b7280" />
                      </button>
                      {tag.status === 'active' && (
                        <button
                          onClick={() => setArchiveConfirm(tag)}
                          title="비활성화"
                          style={{
                            padding: '6px',
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                          }}
                        >
                          <Archive size={14} color="#dc2626" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* V104.1: 페이지네이션 UI */}
        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 0',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
          >
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: currentPage === 1 ? '#f3f4f6' : '#fff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.5 : 1,
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <span style={{ fontSize: '14px', color: '#374151', minWidth: '60px', textAlign: 'center' }}>
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#fff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage === totalPages ? 0.5 : 1,
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* 생성/수정 모달 */}
      {showModal && (
        <TagFormModal
          tag={editingTag}
          tagType={activeTab}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingTag(null);
          }}
          isLoading={isLoading}
        />
      )}

      {/* 비활성화 확인 모달 */}
      {archiveConfirm && (
        <ConfirmModal
          title="태그 비활성화"
          message={`"${archiveConfirm.label}" 태그를 비활성화하시겠습니까?\n기존 프로젝트에 연결된 태그는 유지됩니다.`}
          confirmText="비활성화"
          confirmColor="#dc2626"
          onConfirm={() => handleArchive(archiveConfirm)}
          onCancel={() => setArchiveConfirm(null)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

// ===== 태그 폼 모달 =====

interface TagFormModalProps {
  tag: ProjectTagMaster | null;
  tagType: ProjectTagType;
  onSave: (label: string) => void;
  onClose: () => void;
  isLoading: boolean;
}

function TagFormModal({ tag, tagType, onSave, onClose, isLoading }: TagFormModalProps) {
  const [label, setLabel] = useState(tag?.label || '');
  const tagFormOverlayProps = useModalOverlayClose(onClose);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) {
      alert('태그명을 입력해주세요.');
      return;
    }
    onSave(label.trim());
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      {...tagFormOverlayProps}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          width: '400px',
          maxWidth: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
          {tag ? '태그 수정' : '태그 추가'} - {PROJECT_TAG_TYPE_LABELS[tagType]}
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '6px',
                color: '#374151',
              }}
            >
              태그명 <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="예: QA, 게임, 블록체인"
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
              동일한 이름의 태그는 등록할 수 없습니다. (대소문자 무시)
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '10px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isLoading || !label.trim()}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                cursor: isLoading || !label.trim() ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                fontSize: '14px',
                opacity: isLoading || !label.trim() ? 0.5 : 1,
              }}
            >
              {isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===== 확인 모달 =====

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmText: string;
  confirmColor: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function ConfirmModal({
  title,
  message,
  confirmText,
  confirmColor,
  onConfirm,
  onCancel,
  isLoading,
}: ConfirmModalProps) {
  const confirmOverlayProps = useModalOverlayClose(onCancel);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      {...confirmOverlayProps}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          width: '400px',
          maxWidth: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>{title}</h2>
        <p style={{ color: '#6b7280', whiteSpace: 'pre-line', marginBottom: '20px' }}>{message}</p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              padding: '10px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: confirmColor,
              color: '#fff',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              fontSize: '14px',
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {isLoading ? '처리 중...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
